import { generateId } from '../utils/id'
import type {
  InventoryItem,
  ShoppingListItem,
  Deal,
  Transaction,
  Store,
} from '../types'

const today = new Date().toISOString().split('T')[0]
const daysFromNow = (d: number) => {
  const date = new Date()
  date.setDate(date.getDate() + d)
  return date.toISOString().split('T')[0]
}
const daysAgo = (d: number) => daysFromNow(-d)

// ── Stores ──────────────────────────────────────────────
export const seedStores: Store[] = [
  {
    id: generateId(),
    name: 'Albert Heijn',
    color: '#00A0E2',
    type: 'supermarket',
    priceLevel: 3,
    isFavorite: true,
    isActive: true,
  },
  {
    id: generateId(),
    name: 'Jumbo',
    color: '#FFC800',
    type: 'supermarket',
    priceLevel: 3,
    isFavorite: false,
    isActive: true,
  },
  {
    id: generateId(),
    name: 'Lidl',
    color: '#0050AA',
    type: 'discount',
    priceLevel: 1,
    isFavorite: false,
    isActive: true,
  },
  {
    id: generateId(),
    name: 'Makro',
    color: '#E30613',
    type: 'wholesale',
    priceLevel: 2,
    isFavorite: false,
    isActive: true,
  },
  {
    id: generateId(),
    name: 'Sligro',
    color: '#009639',
    type: 'wholesale',
    priceLevel: 2,
    isFavorite: false,
    isActive: true,
  },
]

const storeId = (name: string) => seedStores.find((s) => s.name === name)!.id

// ── Inventory ───────────────────────────────────────────
export const seedInventory: InventoryItem[] = [
  {
    id: generateId(),
    name: 'Halfvolle melk',
    brand: 'Campina',
    category: 'Zuivel',
    quantity: 3,
    unit: 'liter',
    minQuantity: 2,
    maxQuantity: 10,
    location: 'Koelkast',
    expiryDate: daysFromNow(5),
    lastRestocked: daysAgo(2),
    price: 1.29,
    bulkEligible: true,
    averageShelfLife: 10,
    consumptionRate: 1.5,
    storageRequirement: 1,
    currentStock: 3,
    totalWeight: 3,
    stockStatus: 'medium',
  },
  {
    id: generateId(),
    name: 'Penne pasta',
    brand: 'De Cecco',
    category: 'Droogwaren',
    quantity: 5,
    unit: 'pak',
    minQuantity: 2,
    maxQuantity: 12,
    location: 'Voorraadkast',
    lastRestocked: daysAgo(10),
    price: 1.89,
    bulkEligible: true,
    averageShelfLife: 365,
    consumptionRate: 0.5,
    storageRequirement: 1,
    currentStock: 5,
    totalWeight: 2.5,
    stockStatus: 'high',
  },
  {
    id: generateId(),
    name: 'Kipfilet',
    brand: 'Kip van Oranje',
    category: 'Vlees',
    quantity: 1,
    unit: 'kg',
    minQuantity: 2,
    maxQuantity: 5,
    location: 'Vriezer',
    expiryDate: daysFromNow(2),
    lastRestocked: daysAgo(3),
    price: 8.49,
    bulkEligible: false,
    averageShelfLife: 5,
    consumptionRate: 0.8,
    storageRequirement: 2,
    currentStock: 1,
    totalWeight: 1,
    stockStatus: 'low',
  },
  {
    id: generateId(),
    name: 'Olijfolie extra vierge',
    brand: 'Bertolli',
    category: 'Oliën & Sauzen',
    quantity: 1,
    unit: 'fles',
    minQuantity: 1,
    maxQuantity: 3,
    location: 'Voorraadkast',
    lastRestocked: daysAgo(30),
    price: 5.99,
    bulkEligible: true,
    averageShelfLife: 365,
    consumptionRate: 0.1,
    storageRequirement: 1,
    currentStock: 1,
    totalWeight: 0.75,
    stockStatus: 'medium',
  },
  {
    id: generateId(),
    name: 'Brood volkoren',
    brand: 'Bolletje',
    category: 'Brood & Gebak',
    quantity: 1,
    unit: 'stuk',
    minQuantity: 1,
    maxQuantity: 3,
    location: 'Broodtrommel',
    expiryDate: daysFromNow(3),
    lastRestocked: daysAgo(1),
    price: 2.49,
    bulkEligible: false,
    averageShelfLife: 5,
    consumptionRate: 1,
    storageRequirement: 1,
    currentStock: 1,
    totalWeight: 0.8,
    stockStatus: 'medium',
  },
  {
    id: generateId(),
    name: 'Eieren scharrel',
    brand: 'AH',
    category: 'Zuivel',
    quantity: 6,
    unit: 'stuk',
    minQuantity: 6,
    maxQuantity: 18,
    location: 'Koelkast',
    expiryDate: daysFromNow(14),
    lastRestocked: daysAgo(3),
    price: 2.99,
    bulkEligible: true,
    averageShelfLife: 21,
    consumptionRate: 2,
    storageRequirement: 1,
    currentStock: 6,
    totalWeight: 0.4,
    stockStatus: 'low',
  },
  {
    id: generateId(),
    name: 'Wasmiddel pods',
    brand: 'Ariel',
    category: 'Huishouden',
    quantity: 28,
    unit: 'stuk',
    minQuantity: 10,
    maxQuantity: 60,
    location: 'Bijkeuken',
    lastRestocked: daysAgo(14),
    price: 12.99,
    bulkEligible: true,
    averageShelfLife: 730,
    consumptionRate: 0.5,
    storageRequirement: 1,
    currentStock: 28,
    totalWeight: 0.8,
    stockStatus: 'high',
  },
  {
    id: generateId(),
    name: 'Bananen',
    category: 'Groente & Fruit',
    quantity: 4,
    unit: 'stuk',
    minQuantity: 3,
    maxQuantity: 8,
    location: 'Fruitschaal',
    expiryDate: daysFromNow(4),
    lastRestocked: daysAgo(1),
    price: 1.69,
    bulkEligible: false,
    averageShelfLife: 7,
    consumptionRate: 1.5,
    storageRequirement: 1,
    currentStock: 4,
    totalWeight: 0.5,
    stockStatus: 'medium',
  },
]

// ── Deals ───────────────────────────────────────────────
export const seedDeals: Deal[] = [
  {
    id: generateId(),
    retailerId: storeId('Albert Heijn'),
    itemId: seedInventory[0].id,
    itemName: 'Halfvolle melk 6-pack',
    storeName: 'Albert Heijn',
    originalPrice: 7.74,
    discountedPrice: 5.49,
    startDate: daysAgo(1),
    endDate: daysFromNow(6),
    type: 'bulk',
    minQuantity: 6,
    usageCount: 0,
    totalSavings: 0,
    isActive: true,
    score: 82,
  },
  {
    id: generateId(),
    retailerId: storeId('Jumbo'),
    itemId: seedInventory[1].id,
    itemName: 'Penne pasta 3 voor €4',
    storeName: 'Jumbo',
    originalPrice: 5.67,
    discountedPrice: 4.0,
    startDate: daysAgo(2),
    endDate: daysFromNow(5),
    type: 'promotion',
    minQuantity: 3,
    usageCount: 1,
    totalSavings: 1.67,
    isActive: true,
    score: 75,
  },
  {
    id: generateId(),
    retailerId: storeId('Makro'),
    itemId: seedInventory[6].id,
    itemName: 'Ariel pods 90 stuks',
    storeName: 'Makro',
    originalPrice: 38.97,
    discountedPrice: 24.99,
    startDate: daysAgo(3),
    endDate: daysFromNow(10),
    type: 'bulk',
    minQuantity: 1,
    usageCount: 0,
    totalSavings: 0,
    isActive: true,
    score: 91,
  },
  {
    id: generateId(),
    retailerId: storeId('Lidl'),
    itemId: seedInventory[3].id,
    itemName: 'Olijfolie extra vierge 1L',
    storeName: 'Lidl',
    originalPrice: 5.99,
    discountedPrice: 3.99,
    startDate: today,
    endDate: daysFromNow(3),
    type: 'seasonal',
    usageCount: 0,
    totalSavings: 0,
    isActive: true,
    score: 68,
  },
  {
    id: generateId(),
    retailerId: storeId('Sligro'),
    itemId: seedInventory[2].id,
    itemName: 'Kipfilet 2.5kg doos',
    storeName: 'Sligro',
    originalPrice: 21.23,
    discountedPrice: 16.49,
    startDate: daysAgo(1),
    endDate: daysFromNow(4),
    type: 'bulk',
    minQuantity: 1,
    usageCount: 2,
    totalSavings: 9.48,
    isActive: true,
    score: 77,
  },
]

/**
 * Creates fresh demo deals and connects them to the shopper's current stores.
 * This keeps the demo useful after onboarding, where store IDs are generated anew.
 */
export function createDemoDeals(stores: Store[]): Deal[] {
  const availableStores = stores.filter((store) => store.isActive !== false)
  const targetStores = availableStores.length > 0 ? availableStores : stores

  if (targetStores.length === 0) return []

  return seedDeals.map((deal, index) => {
    const matchingStore = targetStores.find(
      (store) => store.name.toLowerCase() === deal.storeName?.toLowerCase(),
    )
    const targetStore = matchingStore ?? targetStores[index % targetStores.length]

    return {
      ...deal,
      id: generateId(),
      retailerId: targetStore.id,
      storeName: targetStore.name,
      isDemo: true,
    }
  })
}

// ── Shopping List ───────────────────────────────────────
export const seedShoppingList: ShoppingListItem[] = [
  {
    id: generateId(),
    name: 'Kipfilet',
    quantity: 2,
    unit: 'kg',
    estimatedPrice: 16.98,
    store: 'Albert Heijn',
    priority: 'high',
    category: 'Vlees',
    isCompleted: false,
  },
  {
    id: generateId(),
    name: 'Halfvolle melk',
    quantity: 6,
    unit: 'liter',
    estimatedPrice: 5.49,
    deal: seedDeals[0].id,
    store: 'Albert Heijn',
    priority: 'medium',
    category: 'Zuivel',
    isCompleted: false,
  },
  {
    id: generateId(),
    name: 'Tomaten',
    quantity: 500,
    unit: 'gram',
    estimatedPrice: 2.29,
    store: 'Jumbo',
    priority: 'low',
    category: 'Groente & Fruit',
    isCompleted: false,
  },
  {
    id: generateId(),
    name: 'Toiletpapier',
    quantity: 1,
    unit: 'pak',
    estimatedPrice: 6.99,
    store: 'Makro',
    priority: 'medium',
    category: 'Huishouden',
    isCompleted: true,
  },
  {
    id: generateId(),
    name: 'Kaas jong belegen',
    quantity: 1,
    unit: 'stuk',
    estimatedPrice: 4.89,
    store: 'Albert Heijn',
    priority: 'low',
    category: 'Zuivel',
    isCompleted: false,
  },
]

// ── Transactions ────────────────────────────────────────
export const seedTransactions: Transaction[] = [
  {
    id: generateId(),
    store: 'Albert Heijn',
    items: 'Melk, brood, kaas, eieren',
    amount: 23.47,
    date: daysAgo(1),
    category: 'Boodschappen',
    type: 'expense',
    originalPrice: 27.12,
    discountedPrice: 23.47,
    savings: 3.65,
    dealType: 'promotion',
    retailerId: storeId('Albert Heijn'),
  },
  {
    id: generateId(),
    store: 'Makro',
    items: 'Wasmiddel, keukenrol, vaatwastabletten',
    amount: 41.97,
    date: daysAgo(5),
    category: 'Huishouden',
    type: 'expense',
    originalPrice: 56.85,
    discountedPrice: 41.97,
    savings: 14.88,
    dealType: 'bulk',
    retailerId: storeId('Makro'),
  },
  {
    id: generateId(),
    store: 'Jumbo',
    items: 'Pasta, saus, gehakt',
    amount: 12.38,
    date: daysAgo(3),
    category: 'Boodschappen',
    type: 'expense',
    originalPrice: 14.05,
    discountedPrice: 12.38,
    savings: 1.67,
    dealType: 'promotion',
    retailerId: storeId('Jumbo'),
  },
  {
    id: generateId(),
    store: 'Lidl',
    items: 'Groente, fruit, yoghurt',
    amount: 15.62,
    date: daysAgo(7),
    category: 'Boodschappen',
    type: 'expense',
    originalPrice: 15.62,
    discountedPrice: 15.62,
    savings: 0,
    retailerId: storeId('Lidl'),
  },
]
